/**
 * Runtime registry of boards (classes) discovered from live API responses
 * (login `board[]`, joined-boards endpoint, etc.).
 *
 * The static `classCatalog` only knows about the demo classes shipped with the
 * UI. Once we start routing by `boardId` instead of by the short human code
 * (e.g. `/class/351568/posts` instead of `/class/CS101/posts`), `ClassPage`
 * needs a way to resolve that id back to `{ name, code, isPrivate }` so it can
 * render correctly even on a hard refresh.
 */

export interface RegisteredBoard {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly isPrivate?: boolean;
  /**
   * True when the board is archived (lives under Settings → Archived classes).
   * Lets the edit modal open the "Move to profile" branch for boards that are
   * discovered from the archived-classes endpoint rather than the active list.
   */
  readonly archived?: boolean;
}

const byId = new Map<string, RegisteredBoard>();
const byCode = new Map<string, RegisteredBoard>();

export function registerBoard(board: RegisteredBoard): void {
  if (board.id) byId.set(String(board.id), board);
  if (board.code) byCode.set(board.code.toUpperCase(), board);
}

export function registerBoards(boards: readonly RegisteredBoard[]): void {
  boards.forEach(registerBoard);
}

/** Resolve a board by its boardId (preferred) or short code. */
export function getBoardByKey(key: string | number | undefined): RegisteredBoard | undefined {
  if (key == null) return undefined;
  const str = String(key);
  return byId.get(str) ?? byCode.get(str.toUpperCase());
}
